import { useEffect, useState } from 'react'
import { isArcadeThemeId, resolveArcadeTheme, type ArcadeTheme, type ArcadeThemeId } from './themes'

/**
 * Per-viewer arcade theme selection.
 *
 * localStorage is the right store here and deliberately not the server: the
 * choice is a per-browser convenience with no security or governance meaning,
 * it must survive a reload but never needs to reach another device or another
 * person, and pushing it to the settings secret would turn a cosmetic
 * preference into shared state an admin has to reason about. The admin-set
 * DEFAULT is the opposite case — it is one org-wide value, so it goes through
 * the existing settings path (audited) and arrives in the leaderboard payload.
 *
 * Precedence: the viewer's own stored choice, then the admin default, then the
 * current live design. A viewer who has never touched the picker is never
 * surprised by a change they did not make.
 */

const STORAGE_KEY = 'chu-gpt-arcade-theme'

function readStored(): ArcadeThemeId | null {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY)
    return isArcadeThemeId(raw) ? raw : null
  } catch {
    // Private windows and locked-down profiles throw on access. A viewer with
    // no usable storage still gets a working board on the default theme.
    return null
  }
}

function writeStored(id: ArcadeThemeId): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, id)
  } catch {
    // Non-fatal: the choice applies for this page view and is simply not kept.
  }
}

export interface ArcadeThemeState {
  theme: ArcadeTheme
  /** Null until the viewer picks one — that is what lets the admin default win. */
  chosen: ArcadeThemeId | null
  setTheme: (id: ArcadeThemeId) => void
}

/**
 * @param adminDefault the org default from `GET /api/leaderboard`, or null
 *   before the payload lands / when no admin has set one.
 */
export function useArcadeTheme(adminDefault: string | null): ArcadeThemeState {
  const [chosen, setChosen] = useState<ArcadeThemeId | null>(() => readStored())
  const theme = resolveArcadeTheme(chosen ?? adminDefault)
  return {
    theme,
    chosen,
    setTheme: (id: ArcadeThemeId) => {
      writeStored(id)
      setChosen(id)
    },
  }
}

/**
 * Tracks `prefers-reduced-motion` LIVE rather than reading it once at mount.
 * The pre-theme code sampled the media query in the rain effect's mount
 * handler, so a viewer who turned the setting on mid-session kept the
 * animation until they reloaded. Everything else that moves is stopped in CSS;
 * this hook exists for the canvas, which CSS cannot reach.
 */
export function usePrefersReducedMotion(): boolean {
  const [reduced, setReduced] = useState(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches
  })
  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)')
    const onChange = () => setReduced(mq.matches)
    // addEventListener on MediaQueryList is the modern form; older Safari only
    // has addListener. Guard rather than assume, since this runs on whatever
    // browser a CHU laptop happens to have.
    if (typeof mq.addEventListener === 'function') {
      mq.addEventListener('change', onChange)
      return () => mq.removeEventListener('change', onChange)
    }
    mq.addListener?.(onChange)
    return () => mq.removeListener?.(onChange)
  }, [])
  return reduced
}
